import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IPartnerFinSobRow } from '../../model'

import { getFinSobCategories } from './getFinSobCategories'
import { getRevenueAndSobActuals } from './getRevenueAndSobActuals'
import { getRevenueAndSobBaseline } from './getRevenueAndSobBaseline'
import { getRevenueAndSobExpectedExit } from './getRevenueAndSobExpectedExit'
import { getRevenueAndSobMarketGrowth } from './getRevenueAndSobMarketGrowth'
import { getRevenueAndSobPlan } from './getRevenueAndSobPlan'
import { getRevenueAndSobQuota } from './getRevenueAndSobQuota'
import { IRevenueAndSobCategoryRow } from './types'

interface IParams {
  targetRows: IPartnerFinSobRow[]
  actualsRows: IPartnerFinSobRow[]
  quotaRows?: IPartnerFinSobRow[]
  selectedFYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  isYtd: boolean
  currentFQuarter: number
  fQuarterActualsPercentage: number
  /** Whether the selected period/quarter is still open (computed once via `isPeriodOpen`).
   *  Expected exit and actuals never both show — whichever this excludes comes back `null`. */
  isPeriodOpen: boolean
  selectedCategory: string
}

const buildRow = (
  category: string,
  totalCategory: string,
  {
    targetRows,
    actualsRows,
    quotaRows = [],
    selectedFYear,
    actualsType,
    isLocalCurrency,
    isYtd,
    currentFQuarter,
    fQuarterActualsPercentage,
    isPeriodOpen,
  }: IParams
): IRevenueAndSobCategoryRow => {
  const baseline = getRevenueAndSobBaseline({
    rows: actualsRows,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    currentFQuarter,
    fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
    category,
  })

  const plan = getRevenueAndSobPlan({
    rows: targetRows,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    currentFQuarter,
    fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
    category,
  })

  const expectedExit = !isPeriodOpen
    ? { sales: null, sob: null }
    : getRevenueAndSobExpectedExit({
        rows: [...actualsRows, ...targetRows],
        selectedFYear,
        currentFQuarter,
        actualsType,
        isLocalCurrency,
        category,
      })

  const actuals = isPeriodOpen
    ? { sales: null, sob: null }
    : getRevenueAndSobActuals({
        rows: actualsRows,
        selectedFYear,
        actualsType,
        isLocalCurrency,
        category,
      })

  const quota = getRevenueAndSobQuota({
    rows: quotaRows,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    category,
  })

  const marketGrowth = getRevenueAndSobMarketGrowth({
    rows: [...actualsRows, ...targetRows],
    selectedFYear,
    category,
  })

  return {
    category,
    isTotal: category === totalCategory,
    sales: {
      baseline: baseline.sales,
      plan: plan.sales,
      expectedExit: expectedExit.sales,
      actuals: actuals.sales,
      quota: quota.sales,
      marketGrowth: marketGrowth.sales,
    },
    sob: {
      baseline: baseline.sob,
      plan: plan.sob,
      expectedExit: expectedExit.sob,
      actuals: actuals.sob,
      quota: quota.sob,
      marketGrowth: marketGrowth.sob,
    },
  }
}

/** One row per category present in the selected offering's rows, plus a synthetic Total row
 *  (the same offering-wide aggregate the collapsed tile shows) — the data behind the expanded
 *  view's per-category breakdown table. */
export const getRevenueAndSobByCategory = (params: IParams): IRevenueAndSobCategoryRow[] => {
  const categories = getFinSobCategories([
    ...params.targetRows,
    ...params.actualsRows,
    ...(params.quotaRows ?? []),
  ])

  return [
    ...categories.map((category) => buildRow(category, params.selectedCategory, params)),
    buildRow(params.selectedCategory, params.selectedCategory, params), // build the total row (?)
  ]
}
